import type { QuotaPolicy } from '../../shared-types/src/index.js';

export interface QuotaPersistence {
  upsert(policy: QuotaPolicy): Promise<void>;
  list(tenantId: string): Promise<QuotaPolicy[]>;
  /** The policy as stored, for audit `before` payloads. */
  get(tenantId: string, id: string): Promise<QuotaPolicy | undefined>;
  /** Idempotent: `false` means it was already gone, which is still success. */
  delete(tenantId: string, id: string): Promise<boolean>;
}

/** NUL delimiter: it cannot occur in a tenant or policy id, so the composite
 *  key is unambiguous. This store used to be keyed by `policy.id` alone, which
 *  was safe only while ids were operator-chosen; the deterministic credit-limit
 *  ids (`climit:user:<userId>:weekly:all`) are identical across tenants whenever
 *  a subject id is reused, so a shared store could read or overwrite another
 *  tenant's policy. */
function compositeKey(tenantId: string, id: string): string {
  return `${tenantId} ${id}`;
}

export class MemoryQuotaStore implements QuotaPersistence {
  constructor(private readonly policies: Map<string, QuotaPolicy> = new Map()) {}

  async upsert(policy: QuotaPolicy): Promise<void> {
    this.policies.set(compositeKey(policy.tenantId, policy.id), policy);
  }

  async list(tenantId: string): Promise<QuotaPolicy[]> {
    return [...this.policies.values()].filter((policy) => policy.tenantId === tenantId);
  }

  async get(tenantId: string, id: string): Promise<QuotaPolicy | undefined> {
    return this.policies.get(compositeKey(tenantId, id));
  }

  async delete(tenantId: string, id: string): Promise<boolean> {
    return this.policies.delete(compositeKey(tenantId, id));
  }
}

/** Adopt a caller-supplied map (fixtures, tests). Entries keyed the old way —
 *  by bare `policy.id` — are re-keyed so pre-existing fixtures keep working. */
export function quotaPersistenceFromMap(policies: Map<string, QuotaPolicy>): QuotaPersistence {
  for (const [key, policy] of [...policies]) {
    const expected = compositeKey(policy.tenantId, policy.id);
    if (key === expected) continue;
    policies.delete(key);
    policies.set(expected, policy);
  }
  return new MemoryQuotaStore(policies);
}
